import React, { useState } from 'react';
import styled from 'styled-components/macro';
import { Data, DataLabel } from '../styles';
import { Bar } from 'react-chartjs-2';
import { Chart } from 'chart.js';
import 'chart.js/auto';
import { THEME_COLORS } from '../../utils/theme';
import ChartDataLabels from 'chartjs-plugin-datalabels';

const BarChartContainer = styled.div`
  display: flex;
  flex-direction: column;
  @media (min-width: 768px) {
    flex-direction: row;
  }
`;

const LegendContainer = styled.div`
  width: 100%;
  @media (min-width: 768px) {
    width: 33.33%;
  }
`;

interface Props {
  data: number[];
  colNames: string[];
  show_legend?: boolean;
  show_percentages?: boolean;
}

Chart.register(ChartDataLabels);

const BarChart: React.FC<Props> = (props: Props) => {
  // if we want the side-legend, this is the index we display
  const [displayDataIndex, setDisplayDataIndex] = useState<number>(0);

  // make an array colors like '#e6f2ff' * props.data.length
  let colors = Array(props.data.length).fill('#e6f2ff');

  if (props.show_legend) {
    colors[displayDataIndex] = THEME_COLORS.brandPink;
  }
  let data = {
    labels: props.colNames.map(colName => {
      if (window.innerWidth > 768) {
        return colName.split(' ');
      }
      return colName.split(' ')[0].split('/')[0] + '...';
    }),
    datasets: [
      {
        backgroundColor: colors,
        hoverBackgroundColor: THEME_COLORS.brandPink,
        data: props.data,
        borderRadius: 10,
        borderSkipped: false,
      },
    ],
  };

  return (
    <BarChartContainer style={{ display: 'flex' }}>
      <div style={{ width: '100%' }}>
        <Bar
          data={data}
          options={{
            scales: {
              x: {
                grid: {
                  display: false,
                },
                ticks: {
                  color: THEME_COLORS.brandPink,
                  font: {
                    family: 'Work Sans',
                    weight: '500',
                  },
                  crossAlign: 'near',
                  autoSkip: false,
                },
                border: {
                  display: false,
                },
              },
              y: {
                display: false,
                grid: {
                  display: false,
                },
                ticks: {
                  color: THEME_COLORS.brandPink,
                  stepSize: Math.round((1.05 * Math.max.apply(Math, props.data)) / 10 / 5) * 5,
                  maxTicksLimit: props.show_percentages ? 5 : 10,
                },
              },
            },
            onHover(_, elements) {
              if (elements[0]?.index !== undefined) {
                setDisplayDataIndex(elements[0]?.index);
              }
            },
            plugins: {
              legend: {
                display: false,
              },
              tooltip: {
                enabled: !props.show_legend,
                callbacks: {
                  label: function (context: any) {
                    return props.data[context.dataIndex] + '%';
                  },
                  title: function (context: any) {
                    return props.colNames[context[0].dataIndex];
                  },
                },
                displayColors: false,
              },
              datalabels:
                props.show_percentages && window.innerWidth > 768
                  ? {
                      anchor: 'end',
                      align: 'top',
                      formatter: function (value: number) {
                        return value + '%';
                      },
                      font: {
                        weight: 'bold',
                      },
                    }
                  : {
                      display: false,
                    },
            },
          }}
        />
      </div>
      {props.show_legend && (
        <LegendContainer>
          <Data>{props.data[displayDataIndex]}%</Data>
          <DataLabel>{props.colNames[displayDataIndex]}</DataLabel>
        </LegendContainer>
      )}
    </BarChartContainer>
  );
};

export default BarChart;
